import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SubscribeButton from './SubscribeButton';
import newsletterConfig from '../config/newsletterConfig';
import authService from '../services/authService';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const isAuthenticated = authService.isAuthenticated();
  const [userEmail, setUserEmail] = useState('');

  // Get user email if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      try {
        const userData = authService.getCurrentUser();
        if (userData && userData.email) {
          setUserEmail(userData.email);
        }
      } catch (error) {
        console.error('Error loading user data in footer:', error);
      }
    }
  }, [isAuthenticated]);

  return (
    <footer className="footer-cnn">
      <div className="footer-container">
        {/* Newsletter Subscribe Section */}
        {newsletterConfig.display.showInFooter && (
          <div className="footer-subscribe-section">
            <div className="footer-subscribe-content">
              <h3 className="footer-subscribe-title">Stay Informed</h3>
              <p className="footer-subscribe-text">{newsletterConfig.display.ctaText}</p>
              <SubscribeButton variant="footer" />
            </div>
          </div>
        )}

        {/* Footer Links Grid */}
        <div className="footer-grid">
          {/* Crypto Section */}
          <div className="footer-section">
            <h4 className="footer-title">CRYPTO</h4>
            <ul className="footer-links">
              <li><Link to="/crypto">Latest News</Link></li>
              <li><Link to="/search?q=bitcoin">Bitcoin</Link></li>
              <li><Link to="/search?q=ethereum">Ethereum</Link></li>
              <li><Link to="/search?q=market">Market Analysis</Link></li>
              <li><Link to="/search?q=regulation">Regulation</Link></li>
            </ul>
          </div>

          {/* AI Section */}
          <div className="footer-section">
            <h4 className="footer-title">ARTIFICIAL INTELLIGENCE</h4>
            <ul className="footer-links">
              <li><Link to="/ai">Latest News</Link></li>
              <li><Link to="/search?q=machine learning">Machine Learning</Link></li>
              <li><Link to="/search?q=openai">OpenAI</Link></li>
              <li><Link to="/search?q=google">Google AI</Link></li>
              <li><Link to="/search?q=robotics">Robotics</Link></li>
            </ul>
          </div>

          {/* Politics Section */}
          <div className="footer-section">
            <h4 className="footer-title">POLITICS</h4>
            <ul className="footer-links">
              <li><Link to="/politics">Latest News</Link></li>
              <li><Link to="/search?q=congress">Congress</Link></li>
              <li><Link to="/search?q=election">Elections</Link></li>
              <li><Link to="/search?q=policy">Policy</Link></li>
              <li><Link to="/search?q=white house">White House</Link></li>
            </ul>
          </div>

          {/* About Section */}
          <div className="footer-section">
            <h4 className="footer-title">Focus View</h4>
            <ul className="footer-links">
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/contact">Contact</Link></li>
              <li><Link to="/advertise">Advertise With Us</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/editorial-policy">Editorial Policy</Link></li>
              {/* ✅ FIXED: Simplified unsubscribe link with email */}
              {isAuthenticated && userEmail && (
                <li>
                  <Link to={`/unsubscribe?email=${encodeURIComponent(userEmail)}`}>
                    Unsubscribe
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-left">
            <div className="footer-logo">FOCUS VIEW.AI</div>
            <p className="footer-tagline">
              AI-Powered News Aggregation for Crypto, AI & Politics
            </p>
          </div>
          <div className="footer-bottom-right">
            <div className="footer-social">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="footer-copyright">
          <p>© {currentYear} FocusView.ai. All rights reserved. | Powered by AI</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;